/**
 * Email validation for Add User (local-part allows &, ' plus letters, digits, _, +).
 *
 * Unchanged by US 249646: that story scopes only Distribution List emails, and user
 * emails carry the extra constraint of being passed to Cognito as the username
 * (max 128 chars), so widening them is a separate decision.
 */
export const ADD_USER_EMAIL_VALIDATION_REGEXP =
    /^[a-zA-Z0-9_+'&]+([-+.][a-zA-Z0-9_+'&]+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/ //NOSONAR

/**
 * Printable specials allowed in the local-part, exactly as listed by US 249646 AC1:
 * `! # $ % & ' * + - / = ? ^ _ \ { | } ~` plus backtick.
 *
 * That is the RFC 5322 §3.2.3 `atext` set plus backslash. Backslash is not `atext` — RFC 5322
 * lists it under `specials` and RFC 3696 §3 names it as requiring quoting — so an address using
 * it unquoted is not valid per those specs even though AC1 requires accepting it. Recorded here
 * because it is the one place the implementation knowingly diverges from the referenced RFCs.
 *
 * Keep in sync with the backend patterns (AC7, defense in depth): ImportDistributionVO.email
 * and DistributionVO.externalEmails in common-uam-service.
 */
const ATEXT = "A-Za-z0-9!#$%&'*+\\-/=?^_`{|}~\\\\"

/**
 * Dot-atom form: dots only act as separators between non-empty atoms, which is what
 * rejects a leading dot, a trailing dot and consecutive dots (AC4).
 */
const LOCAL_PART = `[${ATEXT}]+(?:\\.[${ATEXT}]+)*`

/** Domain labels start and end alphanumeric, hyphens only inside; at least two labels (AC2). */
const DOMAIN_LABEL = "[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?"
const DOMAIN = `${DOMAIN_LABEL}(?:\\.${DOMAIN_LABEL})+`

/** RFC 5321 §4.5.3.1.3: the 256-octet forward path, less the enclosing angle brackets (AC4). */
const MAX_EMAIL_LENGTH = 254

/**
 * Email validation for distribution external emails (US 249646).
 * Accepts the full RFC 5322 `atext` set in the local-part.
 */
export const ADD_DISTRIBUTION_EMAIL_VALIDATION_REGEXP =
    new RegExp(`^(?=.{1,${MAX_EMAIL_LENGTH}}$)${LOCAL_PART}@${DOMAIN}$`) //NOSONAR
